import * as companyManagementService from "../../../services/company";
import * as settings from "../../../services/settings";

import { Breadcrumb, Button, Card, Col, Form, Input, Row, Spin, Tabs, Typography, notification } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { useTranslation } from "react-i18next";
import ContentType from "../../../../configs/contentType";
import permissions from "../../../../configs/permission";
import { CreateCompanyBanner } from "../../../Components/company/companyBanner";
import { CreateCompanyDetail } from "../../../Components/company/companyDetail";
import { CreateCompanyFormula } from "../../../Components/company/companyFormula";
import { CreateCompanyLine } from "../../../Components/company/companyLine";
import { CreateCompanyNews } from "../../../Components/company/companyNews";
import { CreateCompanyRoutes } from "../../../Components/company/companyRoutes";
import { CreateCompanySales } from "../../../Components/company/companySales";
import BtnHistory from "../../../Components/model/button/history";
import { validateCreateCompany } from "../../../Components/validations/validateCreateCompany";
import { useAccessToken } from "../../../contexts/AccessTokenContext";
import { usePermission } from "../../../hooks/usePermission";
import { resolveImageUrl } from "../../../utils/imageUrl";

const { Title } = Typography;
const isCompanyCode = /^[A-Za-z0-9]+$/;

export const CompanyCreatePage = () => {
  const { accessToken } = useAccessToken();
  const { hasPermission, isCompanyFullAccess } = usePermission();
  const canCompany = (perm) => isCompanyFullAccess || hasPermission(perm);
  const [formCompany] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isDisable, setIsDisable] = useState(true);
  const [isCreate, setIsCreate] = useState(false);
  const [isHiddenBtnCreate, setIsHiddenBtnCreate] = useState(false);
  const [isCheckCreateSuccess, setIsCreateSuccess] = useState(false);
  const { t } = useTranslation("company", "buttons");
  const [dataCompany, setDataCompany] = useState([]);
  const [fileListCompanyImage, setFileListCompanyImage] = useState([]);
  const [fileListCustomerImage, setFileListCustomerImage] = useState([]);
  const location = useLocation();
  const data = location.state;
  // รองรับ reload แล้ว location.state หาย โดยใช้ localStorage
  const [editState, setEditState] = useState(() => {
    const fromNav = location.state;
    if (fromNav) {
      localStorage.setItem("editCompanyData", JSON.stringify(fromNav));
      return fromNav;
    }
    const stored = localStorage.getItem("editCompanyData");
    return stored ? JSON.parse(stored) : null;
  });
  const isEdit = editState?.type === "EDIT";
  const { companyData, type } = editState;

  // Color
  let initColor = "#1677FF";
  const [colorPrimary, setColorPrimary] = useState(initColor);
  const [colorSecondary, setColorSecondary] = useState(initColor);
  // Validate field is required
  const [validateFieldRequired, setValidateFieldRequired] = useState({
    fieldCompanyCode: false,
    fieldCompanyName: false,
    fieldCompanyAddress: false,
    fieldCompanyBillType: false,
    fieldCustomerBillType: false,
    fieldBillPrimaryColor: false,
    fieldBillSecondaryColor: false,
  });
  // notification
  const navigation = useNavigate();
  const [api, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = (type, message, description) => {
    api[type]({
      message: message,
      description: description,
    });
  };

  // keep data tabs
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab") || "1";
  const [activeTab, setActiveTab] = useState(tabFromUrl);

  const setDetailData = async (data) => {
    formCompany.setFieldValue("company_code", data?.company_code);
    formCompany.setFieldValue("company_name", data?.company_name);
    formCompany.setFieldValue("is_credit", data?.is_credit);
    if (data?.type !== "CREATE") {
      if (data?.app_companyimage?.length > 0) {
        let listImagesCompany = [];
        let listImagesCustomer = [];

        const processImages = async () => {
          for (const value of data.app_companyimage) {
            const hasUploadsPath = value.image?.startsWith("uploads/");
            const subUidImage = hasUploadsPath
              ? value.image.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/)?.[0]
              : `${value.image}-${value.id}`;
            const imageUrl = resolveImageUrl(value.image);

            if (value.image.includes("CompanyBill") || value.image.includes("company_images")) {
              listImagesCompany.push({
                uid: subUidImage || `company-${value.id}`,
                name: `image_${value.id}.png`,
                status: "done",
                url: imageUrl,
                thumbUrl: imageUrl,
                id: value.id,
              });
            }
            if (value.image.includes("CustomerBill") || value.image.includes("customer_images")) {
              listImagesCustomer.push({
                uid: subUidImage || `customer-${value.id}`,
                name: `image_${value.id}.png`,
                status: "done",
                url: imageUrl,
                thumbUrl: imageUrl,
                id: value.id,
              });
            }
          }
        };

        await processImages();
        setFileListCompanyImage(listImagesCompany);
        setFileListCustomerImage(listImagesCustomer);
      }
    }

    formCompany.setFieldValue("tax_id", data?.tax_id);
    formCompany.setFieldValue("email", data?.email);
    formCompany.setFieldValue("telephone", data?.telephone);
    formCompany.setFieldValue("company_address", data?.company_address);
    formCompany.setFieldValue("note", data?.note);
    formCompany.setFieldValue("website", data?.website);
    formCompany.setFieldValue("bank_account_company_bill", data?.bank_account_company_bill);
    formCompany.setFieldValue("company_bill_type", data?.company_bill_type);
    formCompany.setFieldValue("bank_account_customer_bill", data?.bank_account_customer_bill);
    formCompany.setFieldValue("customer_bill_type", data?.customer_bill_type);
    formCompany.setFieldValue("customer_bill_primary_color", data?.customer_bill_primary_color);
    formCompany.setFieldValue("customer_bill_secondary_color", data?.customer_bill_secondary_color);
  };

  const isValidateCreateCompanyCode = (_, value) => {
    if (value !== "" && !isCompanyCode.test(value)) {
      setValidateFieldRequired({
        ...validateFieldRequired,
        fieldCompanyCode: false,
      });
      return Promise.reject("Please check from input");
    }
    setValidateFieldRequired({
      ...validateFieldRequired,
      fieldCompanyCode: true,
    });
    return Promise.resolve();
  };

  const isValidateCreateCompanyName = (_, value) => {
    if (value === "") {
      setValidateFieldRequired({
        ...validateFieldRequired,
        fieldCompanyName: false,
      });
      return Promise.reject();
    }
    setValidateFieldRequired({
      ...validateFieldRequired,
      fieldCompanyName: true,
    });
    return Promise.resolve();
  };

  const isValidateCreateCompanyAddress = (_, value) => {
    if (value === "") {
      setValidateFieldRequired({
        ...validateFieldRequired,
        fieldCompanyAddress: false,
      });
      return Promise.reject();
    }
    setValidateFieldRequired({
      ...validateFieldRequired,
      fieldCompanyAddress: true,
    });
    return Promise.resolve();
  };

  const isValidateCreateCompanyBillType = (_, value) => {
    if (value === "") {
      setValidateFieldRequired({
        ...validateFieldRequired,
        fieldCompanyBillType: false,
      });
      return Promise.reject();
    }
    setValidateFieldRequired({
      ...validateFieldRequired,
      fieldCompanyBillType: true,
    });
    return Promise.resolve();
  };

  const isValidateCreateCompanyCustomerBillType = (_, value) => {
    if (value === "") {
      setValidateFieldRequired({
        ...validateFieldRequired,
        fieldCustomerBillType: false,
      });
      return Promise.reject();
    }
    setValidateFieldRequired({
      ...validateFieldRequired,
      fieldCustomerBillType: true,
    });
    return Promise.resolve();
  };

  const isValidateCreateCompanyBillSecondaryColor = (value) => {
    if (value === "") {
      setValidateFieldRequired({
        ...validateFieldRequired,
        fieldBillSecondaryColor: false,
      });
      return Promise.reject();
    }
    setValidateFieldRequired({
      ...validateFieldRequired,
      fieldBillSecondaryColor: true,
    });
    return Promise.resolve();
  };

  const isValidateCreateCompanyBillPrimaryColor = (value) => {
    if (value === "") {
      setValidateFieldRequired({
        ...validateFieldRequired,
        fieldBillPrimaryColor: false,
      });
      return Promise.reject();
    }
    setValidateFieldRequired({
      ...validateFieldRequired,
      fieldBillPrimaryColor: true,
    });
    return Promise.resolve();
  };

  const onClickedTab = (tabKey) => {
    setActiveTab(tabKey);
    setSearchParams({ tab: tabKey });
    setIsHiddenBtnCreate(tabKey !== "1");
  };

  const onSubmit = async () => {
    await formCompany.validateFields();
    const values = await formCompany.getFieldsValue();
    const {
      company_code,
      company_name,
      is_credit,
      tax_id,
      email,
      telephone,
      company_address,
      note,
      website,
      bank_account_company_bill,
      company_bill_type,
      bank_account_customer_bill,
      customer_bill_type,
    } = values || {};
    const { isValid } = validateCreateCompany(values, colorPrimary, colorSecondary);
    if (isValid) {
      const fromData = new FormData();
      fromData.append("company_code", company_code);
      fromData.append("company_name", company_name);
      fromData.append("company_address", company_address || "");
      fromData.append("note", note || "");
      fromData.append("is_credit_data", is_credit || false);
      fromData.append("email", email || "");
      fromData.append("tax_id", tax_id || "");
      fromData.append("telephone", telephone || "");
      fromData.append("website", website || "");
      fromData.append("bank_account_company_bill", bank_account_company_bill || "");
      fromData.append("company_bill_type", company_bill_type || "");
      fromData.append("bank_account_customer_bill", bank_account_customer_bill || "");
      fromData.append("customer_bill_type", customer_bill_type);

      fromData.append(
        "customer_bill_primary_color",
        colorPrimary === initColor ? initColor.substring(1) : colorPrimary.substring(1, 7)
      );
      fromData.append(
        "customer_bill_secondary_color",
        colorSecondary === initColor ? initColor.substring(1) : colorSecondary.substring(1, 7)
      );

      const old_images_company = [];
      fileListCompanyImage.forEach((file) => {
        if (file.id) {
          old_images_company.push(file.id);
        } else if (file.originFileObj) {
          fromData.append("file_company", file.originFileObj);
        }
      });
      fromData.append("old_images_company", JSON.stringify(old_images_company));

      const old_images_customer = [];
      fileListCustomerImage.forEach((file) => {
        if (file.id) {
          old_images_customer.push(file.id);
        } else if (file.originFileObj) {
          fromData.append("file_customer", file.originFileObj);
        }
      });
      fromData.append("old_images_customer", JSON.stringify(old_images_customer));

      setLoading(true);
      try {
        if (type === "CREATE") {
          const response = await companyManagementService.createCompany(accessToken, fromData);
          if (response !== null && response != undefined) {
            openNotificationWithIcon(
              settings.NotificationType.SUCCESS,
              <>{"Success."}</>,
              <>{"Create company success"}</>
            );
            setIsCreateSuccess(true);
            const dataCompany = await companyManagementService.getCompanyById(accessToken, response.id);
            setLoading(false);
            setIsDisable(false);
            await setDetailData(dataCompany);
            setIsCreate(true);
            setDataCompany(dataCompany);
          } else {
            const { message } = settings.getErrorMessage();
            openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
            setLoading(false);
          }
        } else {
          fromData.append("id", companyData.id);
          const response = await companyManagementService.updateCompany(accessToken, fromData);
          if (response !== null || response != undefined) {
            openNotificationWithIcon(
              settings.NotificationType.SUCCESS,
              <>{"Success."}</>,
              <>{"update company success"}</>
            );
            setLoading(false);
          }
        }
      } catch (err) {
        const { message } = settings.getErrorMessage(err);
        openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
        setLoading(false);
      }
    } else {
      openNotificationWithIcon(
        settings.NotificationType.ERROR,
        <>{"ERROR."}</>,
        <>{`Please check data is request create/update company`}</>
      );
      setLoading(false);
    }
  };

  const fetchCompanyById = useCallback(
    async (param) => {
      setLoading(true);
      try {
        const dataCompany = await companyManagementService.getCompanyById(accessToken, param.id);
        await setDetailData(dataCompany);
        setDataCompany(dataCompany);
      } catch (err) {
        console.error("[CompanyCreatePage] fetchCompanyById: ", err);
        const { message } = settings.getErrorMessage(err);
        openNotificationWithIcon(settings.NotificationType.ERROR, <>{"ERROR."}</>, <>{`${message}`}</>);
      }
      setLoading(false);
    },
    [accessToken]
  );

  const items = [
    {
      title: <span>{t("Page_Body_Tab_Menu.Home", { ns: "menus" })}</span>,
    },
    {
      title: <span>{t("Page_Body_Tab_Menu.Menu", { ns: "menus" })}</span>,
    },
    {
      title: <a href="/company">{t("Title_Company")}</a>,
    },
    {
      title: (
        <a href="/company/create" style={{ fontWeight: 600, color: "black" }}>
          {!isEdit ? t("Company_Create.Title_Company_Create") : t("Company_Create.Title_Company_Edit")}
        </a>
      ),
    },
  ];

  useEffect(() => {
    const initEditData = async () => {
      if (accessToken && editState) {
        if (type === "EDIT") {
          if (editState.companyData?.id) {
            fetchCompanyById(editState.companyData);
          } else {
            console.warn("Missing companyData.id");
          }
          await setDetailData(editState.companyData);
          setDataCompany(editState.companyData);
          setIsDisable(false);
          setIsCreateSuccess(false);
        }
      }
    };
    initEditData();
  }, [accessToken, editState]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tab = queryParams.get("tab") || "1";
    setActiveTab(tab);
  }, [location.search]);

  const initialItems = [
    {
      label: t("Company_Create.Create_Detail.Title_Detail"),
      children: (
        <CreateCompanyDetail
          t={t}
          type={type}
          formCompany={formCompany}
          detailData={companyData}
          setColorPrimary={setColorPrimary}
          setColorSecondary={setColorSecondary}
          isValidateCreateCompanyAddress={isValidateCreateCompanyAddress}
          isValidateCreateCompanyBillType={isValidateCreateCompanyBillType}
          isValidateCreateCompanyCustomerBillType={isValidateCreateCompanyCustomerBillType}
          isValidateCreateCompanyBillPrimaryColor={isValidateCreateCompanyBillPrimaryColor}
          isValidateCreateCompanyBillSecondaryColor={isValidateCreateCompanyBillSecondaryColor}
          fieldBillPrimaryColor={validateFieldRequired.fieldBillPrimaryColor}
          fieldBillSecondaryColor={validateFieldRequired.fieldBillSecondaryColor}
          setFileListCompanyImage={setFileListCompanyImage}
          fileListCompanyImage={fileListCompanyImage}
          setFileListCustomerImage={setFileListCustomerImage}
          fileListCustomerImage={fileListCustomerImage}
        />
      ),
      key: "1",
      visible: true,
    },
    {
      label: t("Company_Create.Create_Line.Title_Line"),
      children: (
        <CreateCompanyLine
          type={type}
          t={t}
          detailData={isCreate ? dataCompany : companyData}
          accessToken={accessToken}
        />
      ),
      key: "2",
      disabled: isDisable,
      visible: canCompany(permissions.CompanyLine.VIEW),
    },
    {
      label: t("Company_Create.Create_Routes.Title_Routes"),
      children: canCompany(permissions.CompanyRoute.VIEW) && (
        <CreateCompanyRoutes t={t} detailData={isCreate ? dataCompany : companyData} accessToken={accessToken} />
      ),
      key: "3",
      disabled: isDisable,
      visible: canCompany(permissions.CompanyRoute.VIEW),
    },
    {
      label: t("Company_Create.Create_Formula.Title_Formula"),
      children: canCompany(permissions.CompanyFormula.VIEW) && (
        <CreateCompanyFormula t={t} detailData={isCreate ? dataCompany : companyData} accessToken={accessToken} />
      ),
      key: "4",
      disabled: isDisable,
      visible: canCompany(permissions.CompanyFormula.VIEW),
    },
    {
      label: t("Company_Create.Create_Sales.Title_Sales"),
      children: canCompany(permissions.CompanySale.VIEW) && (
        <CreateCompanySales
          type={type}
          t={t}
          detailData={isCreate ? dataCompany : companyData}
          accessToken={accessToken}
        />
      ),
      key: "5",
      disabled: isDisable,
      visible: true,
    },
    {
      label: t("Company_Create.Create_Banner.Title_Banner"),
      children: canCompany(permissions.CompanyBannerImage.VIEW) && (
        <CreateCompanyBanner
          t={t}
          type={type}
          detailData={isCreate ? dataCompany : companyData}
          accessToken={accessToken}
        />
      ),
      key: "6",
      disabled: isDisable,
      visible: canCompany(permissions.CompanyBannerImage.VIEW),
    },
    {
      label: t("Company_Create.Create_News.Title_News"),
      children: canCompany(permissions.CompanyNews.VIEW) && (
        <CreateCompanyNews
          type={type}
          t={t}
          detailData={isCreate ? dataCompany : companyData}
          accessToken={accessToken}
        />
      ),
      key: "7",
      disabled: isDisable,
      visible: canCompany(permissions.CompanyNews.VIEW),
    },
  ];

  if (type === "EDIT" && !companyData) {
    return <Spin spinning={true} />;
  }

  return (
    <>
      {contextHolder}
      <Spin spinning={loading}>
        <Row>
          <Col span={24}>
            <Breadcrumb items={items}></Breadcrumb>
          </Col>
        </Row>
        <Row className="mt-4" style={{ overflowX: "auto" }}>
          <Col span={24}>
            <div className="flex justify-between">
              <Title level={2}>
                {!isEdit ? t("Company_Create.Title_Company_Create") : t("Company_Create.Title_Company_Edit")}
              </Title>
              {isEdit && (
                <BtnHistory
                  titleRef={dataCompany?.company_name}
                  object_id={dataCompany?.id}
                  content_type_id={[
                    ContentType.Company,
                    ContentType.CompanyFormula,
                    ContentType.CompanyWeightFormula,
                    ContentType.CompanyVolumeFormula,
                    ContentType.CompanyImage,
                    ContentType.CompanyRoute,
                    ContentType.CompanyLine,
                    ContentType.CompanySale,
                    ContentType.CompanyBannerImage,
                    ContentType.CompanyNews,
                    ContentType.CompanyPolicy,
                  ]}
                />
              )}
            </div>
          </Col>
          <Col span={24} style={{ minWidth: 640 }}>
            <Form form={formCompany} layout={{ labelCol: { span: 12 }, wrapperCol: { span: 12 } }}>
              <Row>
                <Col span={12} style={{ minWidth: 200 }}>
                  <Form.Item
                    label={t("Company_Create.Input_Company_Code") + ":"}
                    name="company_code"
                    key="company_code"
                    style={{ marginBottom: "1rem" }}
                    rules={[{ required: true, whitespace: true }, { validator: isValidateCreateCompanyCode }]}
                    hasFeedback
                  >
                    <Input
                      maxLength={3}
                      allowClear="true"
                      placeholder={"company code"}
                      disabled={type == "EDIT" ? true : false}
                      style={{ minWidth: 200 }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ minWidth: 200 }}>
                  <Form.Item
                    label={t("Company_Create.Input_Company_Name") + ":"}
                    name="company_name"
                    key="company_name"
                    style={{ marginBottom: "1rem" }}
                    rules={[{ required: true }, { validator: isValidateCreateCompanyName }]}
                    hasFeedback
                  >
                    <Input
                      allowClear="true"
                      placeholder={"company name"}
                      disabled={type == "EDIT" ? true : false}
                      style={{ minWidth: 200 }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
        <Row align="middle" justify="center">
          <Col span={24}>
            <Card>
              <Tabs
                type="card"
                activeKey={activeTab}
                items={initialItems.filter((item) => item.visible)}
                onChange={onClickedTab}
              />
            </Card>
          </Col>
        </Row>
        {isHiddenBtnCreate === false ? (
          <Row align="middle" justify="end" style={{ marginTop: "20px" }}>
            {(type === "CREATE" && canCompany(permissions.Company.ADD)) ||
            (type === "EDIT" && canCompany(permissions.Company.CHANGE)) ||
            canCompany(permissions.CompanyBillImage.ADD) ||
            canCompany(permissions.CompanyBillImage.CHANGE) ? (
              <Button
                type="primary"
                disabled={
                  type === "CREATE" &&
                  isCheckCreateSuccess &&
                  (!validateFieldRequired.fieldCompanyCode ||
                    !validateFieldRequired.fieldCompanyName ||
                    !validateFieldRequired.fieldCompanyAddress ||
                    !validateFieldRequired.fieldCompanyBillType ||
                    !validateFieldRequired.fieldBillPrimaryColor ||
                    !validateFieldRequired.fieldBillSecondaryColor)
                }
                onClick={onSubmit}
                style={{ marginRight: "10px" }}
              >
                {t("BTN_Save", { ns: "buttons" })}
              </Button>
            ) : undefined}
            <Button
              type="default"
              onClick={() => {
                navigation("/company");
              }}
            >
              {t("BTN_Cancel", { ns: "buttons" })}
            </Button>
          </Row>
        ) : (
          ""
        )}
      </Spin>
    </>
  );
};
